import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import PaintTitle from './PaintTitle';
import { motion } from 'framer-motion';

function ProjectPage({ projects }) {
    const [searchParams] = useSearchParams();
    const focusQuery = searchParams.get('focus') === 'quant' ? '?focus=quant' : '';
    const { projectId } = useParams();
    const project = projects.find((p) => p.id === projectId);
    const projectImages = project?.images;
    const hasImages = (projectImages?.length || 0) > 0;
    const hasGalleryMedia = Boolean(project?.demoVideo || hasImages || project?.externalUrl);

    const [viewMode, setViewMode] = useState('image');
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        if (project) {
            if (project.demoVideo) {
                setViewMode('video');
                if (hasImages) {
                    setSelectedImage(projectImages[0]);
                } else {
                    setSelectedImage(null);
                }
            } else if (hasImages) {
                setViewMode('image');
                setSelectedImage(projectImages[0]);
            } else {
                setViewMode('image');
                setSelectedImage(null);
            }
        }
    }, [project, projectImages, hasImages]);

    if (!project) {
        return (
            <div className="project-page-container">
                <div className="project-not-found">
                    <h2>Project Not Found</h2>
                    <p>Sorry, we couldn't find the project you're looking for.</p>
                    <Link to={`/${focusQuery}`} className="back-link">Back to Home</Link>
                </div>
            </div>
        );
    }

    const layoutClass = project.layout === 'side-by-side' ? 'side-by-side' : '';
    const isShowingVideo = viewMode === 'video' && Boolean(project.demoVideo);
    const isShowingImage = Boolean(selectedImage);
    const isPlaceholderMedia = !isShowingVideo && !isShowingImage && Boolean(project.externalUrl);

    const renderVideo = () => {
        const isYouTube = project.demoVideo.includes('youtube') || project.demoVideo.includes('youtu.be');

        if (isYouTube) {
            let videoSrc = project.demoVideo;

            if (!videoSrc.includes('/embed/')) {
                const videoId = videoSrc.split('v=')[1]?.split('&')[0];
                if (videoId) {
                    videoSrc = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            const cleanParams = '?modestbranding=1&rel=0&iv_load_policy=3&color=white';

            return (
                <iframe
                    className="main-video"
                    src={`${videoSrc}${cleanParams}`}
                    title={project.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            );
        }

        return (
            <video className="main-video" controls autoPlay muted loop>
                <source src={project.demoVideo} type="video/mp4" />
                Your browser does not support the video tag.
            </video>
        );
    };

    const renderMedia = () => {
        if (viewMode === 'video' && project.demoVideo) {
            return renderVideo();
        }

        if (selectedImage) {
            return (
                <img
                    src={selectedImage}
                    alt={`${project.title} main view`}
                    className="main-image"
                />
            );
        }

        if (!project.externalUrl) {
            return null;
        }

        return (
            <div className="project-media-placeholder">
                <span className="project-media-kicker">Featured Build</span>
                <h2>{project.title}</h2>
                <p>
                    This project powers a real production commerce experience built with a Next.js frontend, a Node.js
                    backend, custom operations tooling, automated customer messaging, and AWS-hosted deployment.
                </p>
                <a href={project.externalUrl} target="_blank" rel="noreferrer" className="project-live-link">
                    Visit Live Site
                </a>
            </div>
        );
    };

    return (
        <motion.div
            className="project-page-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <header className="project-header">
                <Link to={`/${focusQuery}#${projectId}`} className="back-link">
                    Back to Projects
                </Link>
                <PaintTitle as="h1">{project.title}</PaintTitle>
                <span>{project.date}</span>
            </header>

            <main className={`project-body ${layoutClass}`}>
                {hasGalleryMedia && (
                    <div className="project-gallery">
                        <div className={`main-image-container ${isPlaceholderMedia ? 'placeholder-media' : ''}`}>
                            {renderMedia()}
                        </div>

                        {(project.demoVideo || hasImages) && (
                            <div className="thumbnail-container">
                                {project.demoVideo && viewMode === 'image' && (
                                    <button
                                        className="thumbnail-image video-thumbnail-btn"
                                        onClick={() => setViewMode('video')}
                                        style={{
                                            background: '#000',
                                            color: 'var(--accent-cyan)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        PLAY VIDEO
                                    </button>
                                )}

                                {projectImages?.map((img, index) => (
                                    <img
                                        key={index}
                                        src={img}
                                        alt={`${project.title} thumbnail ${index + 1}`}
                                        className={`thumbnail-image ${viewMode === 'image' && selectedImage === img ? 'active' : ''}`}
                                        onClick={() => {
                                            setSelectedImage(img);
                                            setViewMode('image');
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="project-details">
                    <h3>About the project</h3>
                    {project.description.split(/\n\s*\n/).map(paragraph => <p key={paragraph}>{paragraph}</p>)}
                    {project.repositoryUrl && <p><a href={project.repositoryUrl} target="_blank" rel="noreferrer" className="project-live-link">View GitHub repository</a></p>}
                    {project.stack && <p className="case-stack"><strong>Built with:</strong> {project.stack}</p>}
                    {project.caseStudy?.map(section => <section className="case-section" key={section.title}><h3>{section.title}</h3><p>{section.text}</p></section>)}
                    {project.externalUrl && (
                        <a href={project.externalUrl} target="_blank" rel="noreferrer" className="project-live-link details-link">
                            Visit Live Site
                        </a>
                    )}
                </div>
            </main>
        </motion.div>
    );
}

export default ProjectPage;
